/**
 * Buying a class pass, a product or a membership outside a class's own
 * checkout: what the hosted booking site's /packages, /product/[slug] and
 * /memberships pages do, shared so the web components (Website v2's in-page
 * purchases) buy exactly the same way.
 *
 * Passes and products go through the basket checkout like everything else
 * (`createBasketCheckoutSession`); these build the basket line each surface
 * adds. Memberships are Stripe subscriptions bought on Stripe's own Checkout
 * page (`POST /widget/app/membership/checkout`).
 *
 * Pure: no store, no fetch. Each surface adds the line to its own basket store
 * (the widget's Zustand store and the web components' store share
 * localStorage["baluu-basket"], so the shapes must agree).
 */

import type { UserFormAnswer } from "../../components/checkout/utils/types";
import { formatBenefit, type FormattableBenefit } from "../memberships/format-benefit";
import type { BasketMode } from "./basket-surface";

// ---------------------------------------------------------------------------
// Class passes (bundle templates)
// ---------------------------------------------------------------------------

/** A class pass as `GET /widget/pages/packages/:school` returns it. */
export type PublicPass = {
  id: string;
  name: string;
  description?: string | null;
  /** Minor units (pence). */
  price: number;
  currency: string;
  totalSessions: number;
  typeOfExpiration?: "afterPurchase" | "timeRange" | string;
  expirationStartDate?: string | null;
  expirationEndDate?: string | null;
  expiresIn?: number | null;
  expirationPeriodType?: "days" | "weeks" | "months" | string | null;
  /** A class the pass can be used for (the server picks one); the basket
   *  line names it as the pass's course. */
  applicableCourseId?: string | null;
};

// A pass's dates are calendar days, stored at midnight UTC: read in the
// browser's zone they'd name the day before anywhere west of UTC.
const dayMonthYear = (iso: string | null | undefined) => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ""
    : new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(date);
};

/** How long a pass lasts: "Valid 1 Jan 2027 – 31 Mar 2027", or "Expires 3
 *  months after purchase" ("1 month", never "1 months"). Empty when the pass
 *  says nothing usable. */
export function passValidityLabel(pass: PublicPass): string {
  if (pass.typeOfExpiration === "timeRange") {
    const from = dayMonthYear(pass.expirationStartDate);
    const to = dayMonthYear(pass.expirationEndDate);
    if (from && to) return `Valid ${from} – ${to}`;
    if (to) return `Valid until ${to}`;
    return "";
  }
  const count = pass.expiresIn ?? 0;
  if (count <= 0) return "";
  const unit = pass.expirationPeriodType || "months";
  return `Expires ${count} ${count === 1 ? unit.replace(/s$/, "") : unit} after purchase`;
}

/** The basket line for a pass (`type: "bundle"`), without the store's own
 *  `id` / `addedAt`. */
export type PassBasketLine = {
  type: "bundle";
  schoolId: string;
  schoolName: string;
  courseId: string;
  courseName: string;
  bundleTemplateId: string;
  bundleName: string;
  bundleDescription: string;
  bundleTotalSessions: number;
  bundleExpiresIn?: number;
  bundleExpirationPeriodType?: string;
  price: number;
  currency: string;
  quantity: 1;
};

export function passBasketLine(pass: PublicPass, school: { _id?: string; name?: string }): PassBasketLine {
  return {
    type: "bundle",
    schoolId: school._id || "",
    schoolName: school.name || "",
    courseId: pass.applicableCourseId || "",
    courseName: pass.name,
    bundleTemplateId: pass.id,
    bundleName: pass.name,
    bundleDescription: pass.description || "",
    bundleTotalSessions: pass.totalSessions,
    bundleExpiresIn: pass.expiresIn ?? undefined,
    bundleExpirationPeriodType: pass.expirationPeriodType ?? undefined,
    price: pass.price,
    currency: pass.currency,
    quantity: 1,
  };
}

// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------

/** What `CheckoutProduct` hands back from its basket-choice step. */
export type ProductCheckoutResult = {
  productId: string;
  productName: string;
  quantity: number;
  currency?: string;
  selectedPostageId?: string;
  isGift?: boolean;
  giftingDetails?: { message: string; recipientName: string; senderName?: string };
  guestDetails?: { firstName: string; lastName: string; email: string; phone: string };
  checkoutGuestsInformation?: Array<{
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    marketingOptIn?: boolean;
    mandatoryTickBox?: boolean;
  }>;
  /** Answers to the product's booking questions (the userform step). */
  userInputs?: UserFormAnswer[];
};

/** A postage option as `GET /widget/pages/product/:slug` returns it. */
export type ProductPostage = { _id?: string; id?: string; name?: string; price?: number; description?: string; estimatedDays?: number };

/** The basket line for a product, without the store's own `id` / `addedAt`. */
export type ProductBasketLine = {
  type: "product";
  schoolId: string;
  schoolName: string;
  productId: string;
  productName: string;
  productSlug?: string;
  quantity: number;
  price: number;
  currency: string;
  imageUrl?: string;
  postageId?: string;
  postageName?: string;
  postagePrice: number;
  postageTotal: number;
  giftingFee: number;
  isGift?: boolean;
  giftingDetails?: ProductCheckoutResult["giftingDetails"];
  guestDetails?: ProductCheckoutResult["guestDetails"];
  checkoutGuestsInformation?: ProductCheckoutResult["checkoutGuestsInformation"];
  userInputs?: ProductCheckoutResult["userInputs"];
};

/**
 * Build a product's basket line from what the product checkout returned.
 *
 * UNITS — `price` is the PER-UNIT price in PENCE, deliberately NOT the flow's
 * `itemData.price`:
 *   * PENCE: every renderer divides by 100, and the server charges in minor
 *     units.
 *   * PER-UNIT, not the line total: both sides of the basket multiply by
 *     quantity. The client total does `effectiveItemPrice(item) * quantity`,
 *     and the server does `totalAmount += currentPrice * item.quantity`
 *     (CreateBasketCheckoutSession), where `currentPrice` is the DB unit price
 *     stamped by validation and always wins over what we send. The flow's
 *     `price` is its total — quantity × unit plus postage and gifting — so
 *     sending it would bill quantity² in the drawer and disagree with the
 *     server's re-price on every add.
 *
 * Postage and gift wrap are display-only stand-ins here (postage per unit,
 * gift wrap once per line) until the first validate replaces them with the
 * server's own resolution, which is what the PaymentIntent is built from.
 */
export function productBasketLine({
  product,
  postages,
  result,
  school,
}: {
  product: {
    _id?: string;
    slug?: string;
    schoolId?: string;
    price?: number;
    currency?: string;
    cover?: string;
    images?: unknown[];
    gifting?: { giftingFee?: number } | null;
  };
  postages: readonly ProductPostage[];
  result: ProductCheckoutResult;
  school: { _id?: string; name?: string } | null | undefined;
}): ProductBasketLine {
  const postage = postages.find((p) => (p._id || p.id) === result.selectedPostageId);
  // Products store images as plain URL strings, so the shared flow's
  // `product.images?.[0]?.url` is always undefined — resolve the cover here.
  const firstImage = product.images?.[0];
  return {
    type: "product",
    schoolId: school?._id || product.schoolId || "",
    schoolName: school?.name || "",
    productId: result.productId,
    productName: result.productName,
    productSlug: product.slug,
    quantity: result.quantity,
    price: product.price ?? 0,
    currency: result.currency || product.currency || "GBP",
    imageUrl: product.cover || (typeof firstImage === "string" ? firstImage : undefined),
    postageId: result.selectedPostageId,
    postageName: postage?.name,
    postagePrice: postage?.price ?? 0,
    postageTotal: (postage?.price ?? 0) * result.quantity,
    giftingFee: result.isGift ? (product.gifting?.giftingFee ?? 0) : 0,
    isGift: result.isGift,
    giftingDetails: result.giftingDetails,
    guestDetails: result.guestDetails,
    checkoutGuestsInformation: result.checkoutGuestsInformation,
    userInputs: result.userInputs,
  };
}

/**
 * A product line's surcharges in pence: postage (charged PER UNIT) plus the
 * gift-wrap fee (charged ONCE per line). Nothing else in the basket has any.
 *
 * `postageTotal` / `giftingFee` are the server's own resolution, stamped by the
 * validate poll from the postage record and the product's gifting config — the
 * same figures CreateBasketCheckoutSession adds to the PaymentIntent. The
 * `postagePrice × quantity` fallback only covers the window between
 * add-to-basket and the first validate.
 */
export function productSurcharge(item: {
  type: string;
  quantity?: number;
  postageTotal?: number;
  postagePrice?: number;
  isGift?: boolean;
  giftingFee?: number;
}): number {
  if (item.type !== "product") return 0;
  const postage = item.postageTotal ?? (item.postagePrice ?? 0) * (item.quantity || 1);
  const gifting = item.isGift ? (item.giftingFee ?? 0) : 0;
  return postage + gifting;
}

/** A summary row for a product's surcharge, in FinalStep's OrderItem shape. */
export type SurchargeLine = { name: string; price: number; lineTotal?: number; count: number; type: "addon" };

/**
 * A product's surcharges as their own summary rows: they're in the grand
 * total (and in the PaymentIntent) but not in the item's per-unit price, so
 * without these the rows wouldn't add up to what the customer pays. Postage
 * is per unit, gift wrap once per line — see `productSurcharge`.
 */
export function productSurchargeLines(item: {
  type: string;
  quantity?: number;
  postageName?: string;
  postageTotal?: number;
  postagePrice?: number;
  isGift?: boolean;
  giftingFee?: number;
}): SurchargeLine[] {
  if (item.type !== "product") return [];
  const lines: SurchargeLine[] = [];
  const postageUnit = item.postagePrice ?? 0;
  const postageTotal = item.postageTotal ?? postageUnit * (item.quantity || 1);
  if (postageTotal > 0) {
    lines.push({
      name: `Postage${item.postageName ? `: ${item.postageName}` : ""}`,
      price: postageUnit,
      lineTotal: postageTotal,
      count: item.quantity || 1,
      type: "addon",
    });
  }
  const giftingFee = item.isGift ? (item.giftingFee ?? 0) : 0;
  if (giftingFee > 0) lines.push({ name: "Gift wrapping", price: giftingFee, count: 1, type: "addon" });
  return lines;
}

// ---------------------------------------------------------------------------
// Memberships
// ---------------------------------------------------------------------------

/** A membership plan as `GET /widget/pages/:schoolSlug/memberships` returns it. */
export type MembershipPlan = {
  id: string;
  name: string;
  description: string | null;
  /** The school's own wording for the badge above the name; empty = default. */
  badgeLabel?: string | null;
  type: "credit" | "unlimited" | "status" | string;
  priceInCents: number;
  currency: string;
  billingInterval: "monthly" | "yearly" | string;
  creditsPerCycle: number | null;
  coverageType: "all" | "specific" | null | string;
  trialPeriodDays: number | null;
  /** How the billing cycle runs for someone joining now (API: summarisePlanCycle). */
  cycle?: {
    cycleAlignment: "signup_date" | "month_start" | string;
    firstPaymentInCents: number;
    firstPeriodLabel: string;
    firstPeriodIsPartial: boolean;
    /** One customer-facing sentence, e.g. "Renews on the 1st of each month. Today: €15.48 for 26 Oct – 31 Oct, then €80.00 on the 1st." */
    description: string;
  } | null;
  /** Only when the request named the signed-in buyer's email. */
  isSubscribed?: boolean;
  subscriptionId?: string | null;
  /** The plan's live benefits (GET /widget/membership-plans). */
  benefits?: Array<FormattableBenefit & { id: string; coverageType?: "all" | "specific" | null | string }>;
};

/** "£25.00" for the plan's price each interval. */
export function formatMembershipPrice(cents: number, currency: string): string {
  try {
    return (cents / 100).toLocaleString("en-GB", { style: "currency", currency });
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/** The small badge above the plan's name: the school's own wording when it
 *  set one, else plain words for the plan type. The enum value "status" is a
 *  Baluu term and never shows to a customer. */
export function membershipPlanBadge(plan: Pick<MembershipPlan, "type" | "badgeLabel">): string {
  const own = plan.badgeLabel?.trim();
  if (own) return own;
  if (plan.type === "credit") return "Credits";
  if (plan.type === "unlimited") return "Unlimited";
  return "Member";
}

/** What the plan gives, as lines under its price. */
export function membershipPerks(plan: MembershipPlan): string[] {
  const per = plan.billingInterval === "yearly" ? "year" : "month";
  const unlimited = (coverage: string | null | undefined) =>
    coverage === "all" ? "Unlimited access to all classes" : "Unlimited access to selected classes";
  const perks: string[] = [];
  if (plan.benefits?.length) {
    // One line per benefit; the school's label leads when it set one.
    for (const b of plan.benefits) {
      const what =
        b.type === "credit"
          ? `${b.creditsPerCycle ?? 0} credits per ${per}`
          : b.type === "unlimited"
            ? unlimited(b.coverageType)
            : formatBenefit({ ...b, label: null });
      perks.push(b.label ? `${b.label}: ${what}` : what);
    }
  } else {
    if (plan.type === "credit" && plan.creditsPerCycle) perks.push(`${plan.creditsPerCycle} credits per ${per}`);
    if (plan.type === "unlimited") perks.push(unlimited(plan.coverageType));
    if (plan.type === "status") perks.push("Unlock exclusive members-only tickets and prices");
  }
  if (plan.trialPeriodDays) perks.push(`${plan.trialPeriodDays}-day free trial`);
  perks.push("Cancel anytime");
  return perks;
}

/** The body of `POST /widget/app/membership/checkout`. The server resolves
 *  the school's Stripe account itself (getEffectiveStripeConnectAccountId). */
export type MembershipCheckoutRequest = {
  membershipPlanId: string;
  schoolId: string;
  buyerEmail?: string;
  successUrl: string;
  cancelUrl: string;
  source?: string;
  sourceInfo?: Record<string, unknown>;
};

/** Stripe's Checkout page for the subscription, from the endpoint's answer
 *  (`{ success, data: { checkoutUrl } }`), or null. */
export function membershipCheckoutUrl(response: unknown): string | null {
  const data = (response as { data?: { checkoutUrl?: unknown } } | null)?.data;
  const url = data?.checkoutUrl;
  return typeof url === "string" && /^https:\/\//.test(url) ? url : null;
}

/** The customer portal for an active subscription, from
 *  `POST /widget/me/membership-portal`'s answer, or null. */
export function membershipPortalUrl(response: unknown): string | null {
  const body = response as { url?: unknown; data?: { url?: unknown } } | null;
  const url = body?.url ?? body?.data?.url;
  return typeof url === "string" && /^https:\/\//.test(url) ? url : null;
}

// ---------------------------------------------------------------------------
// The purchase overlays' names
// ---------------------------------------------------------------------------
// The booking web components' in-page purchases, named once for the bundle
// (webcomponents lib/purchase-overlay.ts) and the pages that open them
// (Website v2's site-chrome/booking.ts), so neither keeps a copy.

export type PurchaseKind = "giftcard" | "pass" | "membership" | "product";

/** Each purchase overlay's element and the event it dispatches on the
 *  document when it closes. */
export const PURCHASE_ELEMENTS: Record<PurchaseKind, { tag: string; closeEvent: string }> = {
  giftcard: { tag: "baluu-giftcard-checkout-overlay", closeEvent: "baluu:giftcard-checkout-close" },
  pass: { tag: "baluu-pass-checkout-overlay", closeEvent: "baluu:pass-checkout-close" },
  membership: { tag: "baluu-membership-checkout-overlay", closeEvent: "baluu:membership-checkout-close" },
  product: { tag: "baluu-product-checkout-overlay", closeEvent: "baluu:product-checkout-close" },
};

/** What opening a purchase overlay takes (the bundle's `openPurchase`). */
export type OpenPurchaseOptions = {
  businessId: string;
  businessSlug: string;
  apiUrl?: string;
  primaryColor?: string;
  /** The school's hosted booking site (the gift card's links). */
  widgetBaseUrl?: string;
  /** A pass's id (pass), a plan's id (membership): that one alone. None
   *  shows all of them. */
  itemId?: string;
  /** The product's slug (product, required). */
  productSlug?: string;
  /** Membership: "success" shows the confirmation after Stripe's Checkout
   *  sent the buyer back. */
  result?: string;
  /** Whether the overlay offers "Add to basket" beside "Buy now" (pass,
   *  product, gift card; a membership is a subscription and never goes in
   *  the basket). See `BasketMode`; none is "off". */
  basket?: BasketMode;
};

/** The query parameter Stripe's Checkout brings a new member back with; the
 *  bundle shows the confirmation when a page loads with it. */
export const MEMBERSHIP_RETURN_PARAM = "baluu_membership";

/** Where the page keeps, for the tab, which school's membership the buyer
 *  went to Stripe's Checkout for (the return address names only the
 *  marker): the components write it as the buyer leaves, and read it back
 *  as they load. Website v2 writes it too when a visitor joined on the
 *  school's hosted memberships page and Stripe sends them back to the site
 *  (plans/websitev2-booking-gaps.md B4). */
export const MEMBERSHIP_RETURN_KEY = "baluu-membership-return";

/** What `MEMBERSHIP_RETURN_KEY` holds. */
export type MembershipReturnContext = Pick<OpenPurchaseOptions, "businessId" | "businessSlug" | "apiUrl" | "primaryColor"> & {
  planId?: string;
};

/** Whether an address is a new member's return from Stripe's Checkout. */
export function isMembershipReturn(href: string): boolean {
  try {
    return new URL(href).searchParams.get(MEMBERSHIP_RETURN_PARAM) === "success";
  } catch {
    return false;
  }
}

/** The query parameter naming the Stripe Checkout Session a new member came
 *  back from. The marker alone proves nothing (anyone can type it): the
 *  confirmation shows only once the API says that session completed for the
 *  school (`GET /widget/app/membership/checkout-session/:id`). */
export const MEMBERSHIP_SESSION_PARAM = "baluu_membership_session";

/** Stripe's placeholder for the session's id in a success URL; it must stay
 *  literal (not percent-encoded) for Stripe to fill it in. */
const CHECKOUT_SESSION_PLACEHOLDER = "{CHECKOUT_SESSION_ID}";

const STRIPE_CHECKOUT_SESSION_ID = /^cs_(?:test|live)_[A-Za-z0-9]{10,200}$/;

/** Where Stripe's Checkout sends a new member: `href` (its query kept, its
 *  hash kept), with the return marker and the session's id. A marker or a
 *  session from an earlier trip is replaced, never stacked. */
export function membershipSuccessUrl(href: string): string {
  const url = new URL(href);
  const hash = url.hash;
  url.hash = "";
  url.searchParams.delete(MEMBERSHIP_SESSION_PARAM);
  url.searchParams.set(MEMBERSHIP_RETURN_PARAM, "success");
  return `${url.toString()}&${MEMBERSHIP_SESSION_PARAM}=${CHECKOUT_SESSION_PLACEHOLDER}${hash}`;
}

/** The Stripe Checkout Session a new member's return names, when the
 *  address is one (the marker, and a session id of Stripe's shape); null
 *  otherwise. */
export function membershipReturnSession(href: string): string | null {
  try {
    const url = new URL(href);
    if (url.searchParams.get(MEMBERSHIP_RETURN_PARAM) !== "success") return null;
    const id = url.searchParams.get(MEMBERSHIP_SESSION_PARAM) ?? "";
    return STRIPE_CHECKOUT_SESSION_ID.test(id) ? id : null;
  } catch {
    return null;
  }
}

/** The address without a membership return's marker and session. */
export function withoutMembershipReturn(href: string): string {
  const url = new URL(href);
  url.searchParams.delete(MEMBERSHIP_RETURN_PARAM);
  url.searchParams.delete(MEMBERSHIP_SESSION_PARAM);
  return url.toString();
}

/** The query parameter Website v2 names the page to come back to with, on
 *  every link it sends to the school's hosted booking site (apps/widget
 *  libs/siteReturn.ts accepts only the school's own site).
 *  plans/websitev2-booking-gaps.md B4. */
export const SITE_RETURN_PARAM = "return";
